/**
 * Lesson Configuration 타입 정의
 * 학습 순서 및 옵션을 계층별로 설정
 */

// 컴포넌트 타입
export type ComponentType =
    | "quiz"
    | "video"
    | "assignment"
    | "reading"
    | "interactive"
    | "discussion"
    | "download";

// 퀴즈 위치
export type QuizLocation = "pre" | "post" | "wrapup" | "aiq" | "checkup";

// 컴포넌트별 옵션
export interface ComponentOptions {
    // 비디오 옵션
    allowSkip?: boolean;
    allowSkipAfterFirstWatch?: boolean;
    allowPlaybackSpeed?: boolean;
    requiredWatchPercent?: number;

    // 퀴즈 옵션
    showAnswerImmediately?: boolean;
    showAnswerAfterSubmit?: boolean;
    allowRetry?: boolean;
    maxAttempts?: number;
    passingScore?: number;
    timeLimitMinutes?: number;
    retryIncorrectOnly?: boolean;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    showHint?: boolean;
    questionCount?: number; // 바람직한 문제 개수 (0이면 전체)
    questionShortageStrategy?: "available_only" | "fill_from_completed"; // 문제 부족 시 전략

    // 과제 옵션
    allowLateSubmission?: boolean;
    latePenaltyPercent?: number;
    maxFileSizeMb?: number;
    allowedFileExtensions?: string[];

    // 생각 정리하기 (Diary-Text) 옵션
    diaryQuizSetId?: number;
}

// 개별 컴포넌트 설정
export interface ComponentConfig {
    type: ComponentType;
    location?: QuizLocation;
    enabled: boolean;
    required: boolean;
    options?: ComponentOptions;
    companyEditable?: boolean; // company admin에서 이 컴포넌트를 보고 수정할 수 있는지 (false면 숨김)
}

// 레슨 간 순서 모드
export type LessonSequenceMode = "sequential" | "flexible";

// 완료 조건 설정
export interface CompletionRules {
    allRequiredMustComplete: boolean;
    minimumScore?: number;
    enforceSequentialProgress: boolean; // 레슨 내 컴포넌트 순차 진행
    // 레슨 간 순서 설정
    lessonSequenceMode?: LessonSequenceMode; // 순차(sequential) vs 자유(flexible)
    lessonSequenceOverridable?: boolean; // 하위 레벨에서 변경 가능 여부
}

// 학습 설정
export interface LearningSettings {
    showDashboard: boolean;
    showProgress: boolean;
    trackLearningTime: boolean;
    enableNotes: boolean;
    enableBookmarks: boolean;
}

// ========================================================================
// Product/Module Level Learning Configuration
// ========================================================================

// 퀴즈 출제 소스
export type QuestionSource = "checkup" | "incorrect" | "all";

// 문제 부족 시 전략
export type ScopedQuestionShortageStrategy = "available_only" | "include_curriculum_incorrect";

// Scope 레벨 퀴즈 옵션
export interface ScopedQuizOptions {
    questionCount: number; // default: Product=20, Module=10
    shuffleQuestions: boolean; // default: true
    passingScore?: number; // 통과 점수 (%)
    timeLimitMinutes?: number; // 시간 제한 (분)
    questionSource?: QuestionSource; // default: "incorrect"
    questionShortageStrategy?: ScopedQuestionShortageStrategy; // default: "available_only"
}

// Product 레벨 활동 설정 (비디오, 퀴즈 또는 둘 다)
export interface ScopedActivityConfig {
    enabled: boolean;
    types: ("video" | "quiz")[];
    videoContentId?: number; // types에 video 포함 시 콘텐츠 ID
    quizOptions?: ScopedQuizOptions;
}

// Module 레벨 활동 설정 (퀴즈만)
export interface ModuleActivityConfig {
    enabled: boolean;
    quizOptions?: ScopedQuizOptions;
}

// Product 레벨 학습 설정
export interface ProductLearningConfig {
    startActivity?: ScopedActivityConfig;
    endActivity?: ScopedActivityConfig;
}

// Module 레벨 학습 설정
export interface ModuleLearningConfig {
    startActivity?: ModuleActivityConfig;
    endActivity?: ModuleActivityConfig;
}

// 전체 Lesson Configuration
export interface LessonConfiguration {
    version: string;
    sequence: ComponentConfig[];
    completionRules: CompletionRules;
    learningSettings: LearningSettings;

    // Product 레벨 학습 (시작/종료 활동)
    productLearning?: ProductLearningConfig;

    // Module 레벨 학습 (시작/종료 활동)
    moduleLearning?: ModuleLearningConfig;

    // 하위 레벨 오버라이드 설정 (상위에서만 저장)
    overrides?: ConfigurationOverrides;
}

// ========================================================================
// Configuration Overrides (하위 레벨 수정 허용 플래그)
// ========================================================================

export interface CompletionRulesOverrides {
    allRequiredMustComplete?: boolean;
    enforceSequentialProgress?: boolean;
    minimumScore?: boolean;
    lessonSequenceMode?: boolean;
}

export interface LearningSettingsOverrides {
    showDashboard?: boolean;
    showProgress?: boolean;
    trackLearningTime?: boolean;
    enableNotes?: boolean;
    enableBookmarks?: boolean;
}

export interface SequenceOverrides {
    orderOverridable?: boolean;
    optionsOverridable?: boolean;
}

export interface ConfigurationOverrides {
    completionRules?: CompletionRulesOverrides;
    learningSettings?: LearningSettingsOverrides;
    sequence?: SequenceOverrides;
    productLearning?: boolean;
    moduleLearning?: boolean;
}

/**
 * 특정 경로의 override 허용 여부 확인
 * overrides가 없으면 모든 항목이 overridable (하위 호환)
 */
export function isOverridable(
    overrides: ConfigurationOverrides | undefined,
    category: keyof ConfigurationOverrides,
    field?: string
): boolean {
    if (!overrides) return true;
    const categoryValue = overrides[category];
    if (categoryValue === undefined) return true;
    if (typeof categoryValue === "boolean") return categoryValue;
    if (field && typeof categoryValue === "object") {
        const fieldValue = (categoryValue as Record<string, boolean | undefined>)[field];
        return fieldValue === undefined ? true : fieldValue;
    }
    return true;
}

// Configuration 소스 정보
export interface ConfigurationSources {
    curriculumId?: number;
    curriculumHasConfig: boolean;
    productId?: number;
    productHasConfig: boolean;
    licenseId?: number;
    licenseHasConfig: boolean;
    classId?: number;
    classHasConfig: boolean;
}

// 최종 Configuration 응답
export interface EffectiveLessonConfigurationResponse {
    configuration: LessonConfiguration;
    sources: ConfigurationSources;
}

// 템플릿 응답
export interface ConfigurationTemplateResponse {
    id: number;
    name: string;
    description?: string;
    configuration: LessonConfiguration;
    isSystemDefault: boolean;
    createdByUserId?: number;
    createdAt: string;
    updatedAt: string;
}

// 템플릿 목록 응답
export interface ConfigurationTemplateListResponse {
    templates: ConfigurationTemplateResponse[];
}

// 템플릿 생성/수정 요청
export interface ConfigurationTemplateRequest {
    name: string;
    description?: string;
    configuration: LessonConfiguration;
}

// Configuration 설정 요청
export interface LessonConfigurationRequest {
    configuration: LessonConfiguration;
}

// 기본 Configuration 생성 헬퍼
export const createDefaultConfiguration = (): LessonConfiguration => ({
    version: "1.0",
    sequence: [
        {
            type: "quiz",
            location: "pre",
            enabled: true,
            required: false,
            options: {
                showAnswerImmediately: false,
                allowRetry: true,
                maxAttempts: 3,
            },
        },
        {
            type: "video",
            enabled: true,
            required: true,
            options: {
                allowSkip: false,
                allowSkipAfterFirstWatch: true,
                allowPlaybackSpeed: true,
                requiredWatchPercent: 90,
            },
        },
        {
            type: "quiz",
            location: "post",
            enabled: true,
            required: true,
            options: {
                showAnswerImmediately: true,
                allowRetry: true,
                maxAttempts: 0,
                passingScore: 70,
            },
        },
        {
            type: "quiz",
            location: "wrapup",
            enabled: false,
            required: false,
            options: {
                showAnswerImmediately: true,
                allowRetry: true,
                maxAttempts: 0,
                passingScore: 70,
            },
        },
        {
            type: "quiz",
            location: "aiq",
            enabled: false,
            required: false,
            options: {
                showAnswerImmediately: true,
                allowRetry: true,
                maxAttempts: 0,
                passingScore: 70,
            },
        },
        {
            type: "assignment",
            enabled: false,
            required: false,
        },
    ],
    completionRules: {
        allRequiredMustComplete: true,
        enforceSequentialProgress: true,
        lessonSequenceMode: "sequential",
        lessonSequenceOverridable: true,
    },
    learningSettings: {
        showDashboard: true,
        showProgress: true,
        trackLearningTime: true,
        enableNotes: true,
        enableBookmarks: true,
    },
});

// 컴포넌트 타입 라벨
export const COMPONENT_TYPE_LABELS: Record<ComponentType, string> = {
    quiz: "퀴즈",
    video: "비디오",
    assignment: "생각 정리하기",
    reading: "읽기 자료",
    interactive: "인터랙티브",
    discussion: "토론",
    download: "다운로드",
};

// 퀴즈 위치 라벨
export const QUIZ_LOCATION_LABELS: Record<QuizLocation, string> = {
    pre: "Pre-Quiz (사전 평가)",
    post: "Post-Quiz (사후 평가)",
    wrapup: "Wrapup Quiz",
    aiq: "AIQ 퀴즈",
    checkup: "Checkup Quiz",
};

// 컴포넌트 아이콘 (Lucide 아이콘 이름)
export const COMPONENT_TYPE_ICONS: Record<ComponentType, string> = {
    quiz: "ClipboardList",
    video: "Play",
    assignment: "FileText",
    reading: "BookOpen",
    interactive: "MousePointer2",
    discussion: "MessageSquare",
    download: "Download",
};
